import React from "react";
import { PerformanceLevelView } from "../../types";
import { ActiveSlice } from "./reportCardModel";

interface Props {
  /** The slice under the pointer or keyboard focus, or null when none is. */
  active: ActiveSlice | null;
  /**
   * The band scheme every criterion on the card shares, so the resting copy can
   * name the ring thresholds. Null when they disagree, in which case no numbers
   * are quoted rather than quoting one criterion's at all of them.
   */
  bands: PerformanceLevelView[] | null;
}

/**
 * The line beneath the two charts. It is a fixed minimum height so swapping
 * between the resting copy and a slice's detail never reflows the page.
 */
const ReadoutBar: React.FC<Props> = ({ active, bands }) => {
  const thresholds = (bands ?? [])
    .slice()
    .sort((a, b) => a.minGrade - b.minGrade)
    .slice(1)
    .map((level) => level.minGrade);

  if (active) {
    return (
      <p className="readout-bar" aria-live="polite">
        <span className="ro-name">{active.name}</span>
        <span className="ro-meta">
          <b>{Math.round(active.grade)}</b> / 100
          {active.bandName ? `  ·  ${active.bandName}` : ""}
          {"  ·  weight "}
          <b>{active.weightPercent}%</b>
        </span>
        <span className="ro-hint">Click to jump to this criterion.</span>
      </p>
    );
  }

  return (
    <p className="readout-bar" aria-live="polite">
      <span className="ro-meta">
        Slice width is the criterion&rsquo;s weight; length is its grade.
        {thresholds.length > 0
          ? ` Arcs mark the band thresholds — ${thresholds.join(", ")}.`
          : " Arcs mark each criterion’s own band thresholds."}
      </span>
      <span className="ro-hint">
        Point at a slice for its grade. Click one to jump to the detail.
      </span>
    </p>
  );
};

export default ReadoutBar;
